import { useDisclosure } from '@chakra-ui/react';
import {
  AssociationEntity,
  CustomFieldEntityEnum,
  ICreditNoteSchema,
  ICustomFieldRecordSchema,
  TransactableSourceType,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Link as ReactDomLink } from 'react-router-dom';
import {
  useGetAccountById,
  useGetCreditNotesById,
} from '../../../../api/accountsService';
import { accountServiceQueryKeys } from '../../../../api/queryKeysService';
import { ExternalAssociationsIcons } from '../../../../components/ExternalAssociations/ExternalAssociationsIcons';
import {
  MBox,
  MFlex,
  MLink,
  MPageContainer,
  MPageHeader,
  MText,
} from '../../../../components/Monetize';
import { MEntityStatusBadge } from '../../../../components/Monetize/MEntityStatusBadge';
import { ROUTES } from '../../../../constants';
import { CreditStatusEnumDisplay } from '../../../../constants/credits';
import { PAPER_PAGE_MAX_WIDTH } from '../../../../constants/quotes';
import {
  getAccountCreditsRoute,
  getInvoiceDetailRoute,
  getQuoteReviewRoute,
} from '../../../../constants/routes';
import { CreditActions } from '../CreditActions';
import { CreditAdditionalFieldsBanner } from '../CreditAdditionalFieldsBanner';
import { CreditNoteFormEditModal } from './CreditNoteFormEditModal';
import { PreviewCreditNotePdfDrawer } from './PreviewCreditNotePdfDrawer';
import { ViewCreditNoteHtml } from './ViewCreditNoteHtml';

const ACTION_META = {
  DOWNGRADE: {
    label: 'downgrade',
    buildUrl: getQuoteReviewRoute,
  },
  CREDIT_REBILL: {
    label: 'credit and re-bill',
    buildUrl: getInvoiceDetailRoute,
  },
  CREDIT_NOTE: {
    label: 'invoice',
    buildUrl: getInvoiceDetailRoute,
  },
} as const;

export const ViewCreditNote = () => {
  const { creditNoteId, accountId = '' } = useParams();
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();

  const { data: accountDetails } = useGetAccountById(accountId, {
    enabled: !!accountId,
    meta: {
      showErrorToast: true,
      navigateTo: accountId && ROUTES.getAccountCreditsRoute(accountId),
    },
  });

  /**
   * Manual cache check to prevent duplicate API calls.
   * While React Query should automatically handle cached data, it doesn't always work
   * reliably in this scenario when navigating from IdPrefixNavigator. We manually check
   * the cache and use the data as initialData while disabling the query to ensure
   * no duplicate requests are made.
   */
  const existingCacheData = creditNoteId
    ? queryClient.getQueryData<ICreditNoteSchema>(
        accountServiceQueryKeys.creditNotes.creditNoteById(creditNoteId),
      )
    : undefined;

  const { isLoading, data: creditNote } = useGetCreditNotesById(creditNoteId!, {
    enabled: !!creditNoteId && !existingCacheData,
    initialData: existingCacheData,
    meta: {
      showErrorToast: true,
      navigateTo: accountId && ROUTES.getAccountCreditsRoute(accountId),
    },
  });

  const [customFields, setCustomFields] = useState<ICustomFieldRecordSchema>(
    () => creditNote?.customFields || {},
  );

  useEffect(() => {
    if (creditNote?.customFields) {
      setCustomFields(creditNote.customFields);
    }
  }, [creditNote?.customFields]);

  const handleOnCloseModal = (data: ICustomFieldRecordSchema) => {
    setCustomFields(data);
    onClose();
  };

  const isDetailBannerEditable =
    creditNote && creditNote?.voidReason !== null
      ? false
      : Object.keys(customFields).length > 0;

  const extraSubtitleParts: ReactNode[] = [];
  const { action, sourceId } = creditNote?.creditNoteMetadata ?? {};
  const meta = ACTION_META[action as keyof typeof ACTION_META];

  if (meta && sourceId) {
    extraSubtitleParts.push(
      <MText>
        Generated from{' '}
        <MLink asChild>
          <ReactDomLink to={meta.buildUrl(sourceId)}>{meta.label}</ReactDomLink>
        </MLink>
      </MText>,
    );
  }

  return (
    <MPageContainer height="auto" enableAccountSearch>
      <MPageHeader
        hasBackButton
        title="View Credit Note"
        parentLink={getAccountCreditsRoute(
          creditNote ? creditNote?.accountId : '',
        )}
        parentLinkTitle={creditNote && creditNote.accountSummary.accountName}
        copyUrl
        id={creditNote?.id!}
        tag={
          creditNote && (
            <MEntityStatusBadge
              status={CreditStatusEnumDisplay[creditNote.status]}
            />
          )
        }
        extraSubtitleParts={extraSubtitleParts}
        recordAuditInfo={creditNote}
        extraBadges={
          creditNote?.id
            ? [
                <ExternalAssociationsIcons
                  key="ExternalAssociationsIcons"
                  entity={AssociationEntity.CREDIT_NOTE}
                  recordId={creditNote.id}
                />,
              ]
            : undefined
        }
      >
        <MFlex
          alignContent="center"
          alignItems="center"
          columnGap={3}
          display={['none', 'none', 'flex']}
        >
          {!!creditNote && (
            <PreviewCreditNotePdfDrawer creditNoteId={creditNote.id} />
          )}
          {!!creditNote?.id && (
            <CreditActions
              sourceType={TransactableSourceType.creditNote}
              accountId={creditNote.accountId}
              applicationsSourceId={creditNote.credit.id}
              amount={creditNote.amount}
              appliedAmount={creditNote.credit.amountApplied}
              currency={creditNote.currency}
              billGroupId={creditNote.billGroupId}
              creditNoteId={creditNote.id}
              creditStatus={creditNote.status}
            />
          )}
        </MFlex>
      </MPageHeader>
      <MBox flex="1" w="full" pb={8}>
        <MBox mx="auto" mt={2.5} maxW={PAPER_PAGE_MAX_WIDTH} w="full" mb="2">
          <CreditAdditionalFieldsBanner
            invoice={creditNote?.invoice}
            billGroupId={creditNote?.billGroupId}
            isLoading={isLoading}
            customFields={customFields}
            isEditable={isDetailBannerEditable}
            reason={creditNote?.description || ''}
            onOpen={onOpen}
            voidReason={creditNote?.voidReason ?? ''}
            creditType={CustomFieldEntityEnum.CREDIT_NOTE}
          />
          <MBox
            w="full"
            mt={6}
            borderWidth={0.5}
            borderColor="tBlue.hover"
            background="tWhite.titanWhite"
          >
            {!!creditNote && (
              <ViewCreditNoteHtml
                creditNoteId={creditNote.id}
                modifyDate={creditNote?.modifyDate}
              />
            )}
          </MBox>
        </MBox>
      </MBox>
      {isOpen && accountDetails && creditNote && (
        <CreditNoteFormEditModal
          account={accountDetails}
          creditNote={creditNote}
          onClose={onClose}
          onSave={handleOnCloseModal}
        />
      )}
    </MPageContainer>
  );
};
